import { useEffect, useState, useCallback } from "react";
import { Languages, Plus, KeyRound, Save, Sparkles, Loader2, Check, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import {
  getLanguages, adminAddLanguage, adminGetLlmSettings, adminSaveLlmSettings,
  adminGetSmtpSettings, adminSaveSmtpSettings, adminTestSmtp,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SUGGESTED = [
  { code: "nl", name: "Dutch" }, { code: "sv", name: "Swedish" },
  { code: "pl", name: "Polish" }, { code: "fi", name: "Finnish" },
  { code: "es", name: "Spanish" }, { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" }, { code: "da", name: "Danish" },
];

const PROVIDER_MODELS = {
  openai: ["gpt-4o-mini", "gpt-4o", "gpt-5", "gpt-5-mini", "gpt-5.2"],
  anthropic: ["claude-sonnet-4-5", "claude-haiku-4-5", "claude-opus-4-5-20251101"],
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-3-flash-preview"],
};

export const AdminSettings = () => {
  const [languages, setLanguages] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const [llm, setLlm] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4o-mini");
  const [apiKey, setApiKey] = useState("");
  const [savingLlm, setSavingLlm] = useState(false);

  // SMTP settings (for backorder notification emails)
  const [smtp, setSmtp] = useState({
    host: "", port: 587, username: "", from_email: "", to_email: "", use_tls: true, enabled: false,
  });
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpMeta, setSmtpMeta] = useState({ password_set: false, password_preview: "" });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  const loadLanguages = useCallback(async () => {
    try {
      const d = await getLanguages();
      setLanguages(d.languages || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadLanguages();
    adminGetLlmSettings()
      .then((d) => { setLlm(d); setProvider(d.provider); setModel(d.model); })
      .catch(() => {});
    adminGetSmtpSettings()
      .then((d) => {
        setSmtp({
          host: d.host || "", port: d.port || 587, username: d.username || "",
          from_email: d.from_email || "", to_email: d.to_email || "",
          use_tls: d.use_tls ?? true, enabled: d.enabled ?? false,
        });
        setSmtpMeta({ password_set: d.password_set, password_preview: d.password_preview });
      })
      .catch(() => {});
  }, [loadLanguages]);

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const payload = { ...smtp };
      // Only send password when the admin typed a new one (blank = keep existing).
      if (smtpPassword.trim()) payload.password = smtpPassword.trim();
      await adminSaveSmtpSettings(payload);
      toast.success("Email (SMTP) settings saved");
      setSmtpPassword("");
      const fresh = await adminGetSmtpSettings();
      setSmtpMeta({ password_set: fresh.password_set, password_preview: fresh.password_preview });
    } catch {
      toast.error("Could not save SMTP settings");
    } finally {
      setSavingSmtp(false);
    }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const r = await adminTestSmtp();
      if (r.ok) toast.success("Test email sent", { description: r.message });
      else toast.error("Test failed", { description: r.message });
    } catch {
      toast.error("Could not run SMTP test");
    } finally {
      setTestingSmtp(false);
    }
  };

  const addLanguage = async (code, name) => {
    code = (code || "").toLowerCase().trim();
    if (!code || code.length < 2) { toast.error("Enter a valid language code (e.g. nl)"); return; }
    setAdding(true);
    toast.info(`Generating ${name || code} translations…`, { description: "The AI is translating the UI. This can take a few seconds." });
    try {
      await adminAddLanguage({ code, name: name || undefined });
      toast.success(`${name || code.toUpperCase()} added`, { description: "Language is now available in the site switcher." });
      setNewCode(""); setNewName("");
      await loadLanguages();
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 503 ? "LLM not configured" : "Could not add language");
    } finally {
      setAdding(false);
    }
  };

  const saveLlm = async () => {
    setSavingLlm(true);
    try {
      const payload = { provider, model };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const d = await adminSaveLlmSettings(payload);
      toast.success("LLM settings saved", {
        description: d.using_custom_key ? "Using your custom key." : "Using the development server key.",
      });
      setApiKey("");
      const fresh = await adminGetLlmSettings();
      setLlm(fresh);
    } catch {
      toast.error("Could not save LLM settings");
    } finally {
      setSavingLlm(false);
    }
  };

  const clearCustomKey = async () => {
    setSavingLlm(true);
    try {
      await adminSaveLlmSettings({ provider, model, api_key: "" });
      toast.success("Reverted to development server key");
      const fresh = await adminGetLlmSettings();
      setLlm(fresh);
    } catch {
      toast.error("Could not update");
    } finally {
      setSavingLlm(false);
    }
  };

  const existingCodes = new Set(languages.map((l) => l.code));

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="admin-settings-panel">
      {/* Languages */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold">Languages</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Add a language and the AI generates translations for the entire UI (and page/product content on demand).
        </p>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-language-list">
          {languages.map((l) => (
            <Badge key={l.code} variant="secondary" className="gap-1.5" data-testid={`admin-lang-${l.code}`}>
              <span className="uppercase">{l.code}</span> {l.name}
              {l.builtin && <Check className="h-3 w-3 text-chart-2" />}
            </Badge>
          ))}
        </div>

        <div className="mt-5 space-y-3 rounded-lg border border-dashed border-border p-4">
          <Label className="text-xs">Add a new language</Label>
          <div className="flex gap-2">
            <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="Code (e.g. nl)"
              maxLength={5} className="w-32" data-testid="admin-new-lang-code" />
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (optional)"
              className="flex-1" data-testid="admin-new-lang-name" />
            <Button onClick={() => addLanguage(newCode, newName)} disabled={adding} data-testid="admin-add-lang-button">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {SUGGESTED.filter((s) => !existingCodes.has(s.code)).map((s) => (
              <button key={s.code} onClick={() => addLanguage(s.code, s.name)} disabled={adding}
                data-testid={`admin-suggest-lang-${s.code}`}
                className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:opacity-50">
                + {s.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* LLM settings */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold">AI / LLM Provider</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Used for content generation &amp; translation. Defaults to the development server key, or plug in your own.
        </p>

        <div className="mt-3 rounded-lg bg-secondary px-3 py-2 text-xs" data-testid="admin-llm-current">
          Current: <span className="font-medium">{llm?.provider || "—"} / {llm?.model || "—"}</span>
          {" · "}
          {llm?.using_custom_key
            ? <span className="text-chart-3">Custom key ({llm?.key_preview})</span>
            : <span className="text-chart-2">Development server key</span>}
        </div>

        <div className="mt-4 grid gap-3">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v); setModel(PROVIDER_MODELS[v][0]); }}>
              <SelectTrigger className="mt-1" data-testid="admin-llm-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                <SelectItem value="gemini">Google (Gemini)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="mt-1" data-testid="admin-llm-model"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(PROVIDER_MODELS[provider] || []).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3 w-3" /> Custom API key (optional)</Label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="Leave blank to use the development server key" className="mt-1 font-mono"
              data-testid="admin-llm-key" />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveLlm} disabled={savingLlm} data-testid="admin-llm-save">
              {savingLlm ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
            </Button>
            {llm?.using_custom_key && (
              <Button variant="outline" onClick={clearCustomKey} disabled={savingLlm} data-testid="admin-llm-clear-key">
                Use development server key
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* SMTP / Email settings (backorder notifications) */}
      <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2" data-testid="admin-smtp-panel">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold">Email (SMTP) — Backorder Notifications</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          When enabled, new backorder requests are emailed to your team. Fill in your SMTP provider
          details (e.g. from your host or a service like SendGrid/Mailgun). Leave the password blank to keep the existing one.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">SMTP host</Label>
            <Input value={smtp.host} onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))}
              placeholder="smtp.yourprovider.com" className="mt-1 font-mono" data-testid="smtp-host" />
          </div>
          <div>
            <Label className="text-xs">Port</Label>
            <Input type="number" value={smtp.port} onChange={(e) => setSmtp((s) => ({ ...s, port: Number(e.target.value) || 587 }))}
              placeholder="587" className="mt-1 font-mono" data-testid="smtp-port" />
          </div>
          <div>
            <Label className="text-xs">Username</Label>
            <Input value={smtp.username} onChange={(e) => setSmtp((s) => ({ ...s, username: e.target.value }))}
              placeholder="apikey / login" className="mt-1 font-mono" data-testid="smtp-username" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3 w-3" /> Password
              {smtpMeta.password_set && <span className="text-muted-foreground">(saved: {smtpMeta.password_preview})</span>}
            </Label>
            <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)}
              placeholder={smtpMeta.password_set ? "•••••• (leave blank to keep)" : "SMTP password"}
              className="mt-1 font-mono" data-testid="smtp-password" />
          </div>
          <div>
            <Label className="text-xs">From email</Label>
            <Input value={smtp.from_email} onChange={(e) => setSmtp((s) => ({ ...s, from_email: e.target.value }))}
              placeholder="shop@boomerang.ie" className="mt-1 font-mono" data-testid="smtp-from" />
          </div>
          <div>
            <Label className="text-xs">Notify (to) email</Label>
            <Input value={smtp.to_email} onChange={(e) => setSmtp((s) => ({ ...s, to_email: e.target.value }))}
              placeholder="orders@boomerang.ie" className="mt-1 font-mono" data-testid="smtp-to" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch id="smtp-tls" checked={smtp.use_tls} onCheckedChange={(v) => setSmtp((s) => ({ ...s, use_tls: v }))} data-testid="smtp-tls" />
            <Label htmlFor="smtp-tls" className="text-sm">Use STARTTLS</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="smtp-enabled" checked={smtp.enabled} onCheckedChange={(v) => setSmtp((s) => ({ ...s, enabled: v }))} data-testid="smtp-enabled" />
            <Label htmlFor="smtp-enabled" className="text-sm">Enable email notifications</Label>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button onClick={saveSmtp} disabled={savingSmtp} data-testid="smtp-save">
            {savingSmtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
          </Button>
          <Button variant="outline" onClick={testSmtp} disabled={testingSmtp} data-testid="smtp-test">
            {testingSmtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send test email
          </Button>
        </div>
      </div>
    </div>
  );
};
